/**
 * DocumentToolbar — ЄДИНИЙ тулбар для editor'ів ДОКУМЕНТІВ (Закон дизайну #6).
 *
 * Фіксований склад/порядок/розташування кнопок зліва направо:
 *   До списку → Зберегти → Провести(/Розпровести) → Видалити → Друкувати
 *
 * Текст-only, БЕЗ іконок/емодзі (Закон дизайну #7).
 * Presentational: НЕ містить mutation/api — editor володіє логікою і передає
 * колбеки + флаги. Це усуває розбіжність, яку давав старий CancelEditorIntegration.
 *
 * Cancel («Скасувати документ» → CANCELLED) скасовано як концепція —
 * видалення документів логуються у core.audit_log на backend (record_audit).
 *
 * Контекст-правила:
 *  - isNew: показати До списку + Зберегти + Провести (save+post одним кліком,
 *    рішення власника); сховати Розпровести/Видалити/Друкувати.
 *  - postable=false: сховати Провести/Розпровести (документ не проводиться).
 *  - deletable=false: сховати Видалити (наприклад розрахунки).
 *  - onPrint заданий → показати Друкувати (лише де є друкована форма).
 */

interface DocumentToolbarProps {
  /** Кнопка «До списку» — перша зліва (editor: () => navigate(listPath)). */
  onBackToList: () => void;

  /** «Зберегти». */
  onSave: () => void;
  saveDisabled?: boolean;
  savePending?: boolean;

  /** Проведення. postable=false → Провести/Розпровести сховані. */
  postable?: boolean;
  isPosted?: boolean;
  onPost?: () => void;
  onUnpost?: () => void;
  postDisabled?: boolean;
  postPending?: boolean;
  unpostPending?: boolean;
  /** Підказка для «Провести» (напр. «Зберігає та проводить»). */
  postTitle?: string;

  /** «Видалити». deletable=false → кнопка схована. confirm — у editor'і (onDelete). */
  deletable?: boolean;
  onDelete?: () => void;
  deleteDisabled?: boolean;
  deletePending?: boolean;
  deleteTitle?: string;

  /** «друк» — рендериться ЛИШЕ якщо заданий (editor: () => navigate(printPath)).
   *  Назву форми (Т-1/Т-5…) у кнопці НЕ пишемо — лише «друк». */
  onPrint?: () => void;

  /** new-документ: сховати Розпровести/Видалити/Друкувати (Провести лишається — save+post). */
  isNew?: boolean;
}

export default function DocumentToolbar({
  onBackToList,
  onSave, saveDisabled, savePending,
  postable = true, isPosted = false, onPost, onUnpost,
  postDisabled, postPending, unpostPending, postTitle,
  deletable = true, onDelete, deleteDisabled, deletePending, deleteTitle,
  onPrint,
  isNew = false,
}: DocumentToolbarProps) {
  // Тексти кнопок — лише з малої літери (Закон дизайну #7).
  return (
    <div style={{ display: 'flex', gap: '0.5rem' }}>
      <button onClick={onBackToList}>до списку</button>

      <button
        className="primary"
        onClick={onSave}
        disabled={saveDisabled || savePending}
      >
        зберегти
      </button>

      {/* провести / розпровести. На new — лише провести (save+post одним кліком). */}
      {postable && (
        isPosted && !isNew ? (
          <button onClick={onUnpost} disabled={unpostPending}>
            розпровести
          </button>
        ) : !isPosted ? (
          <button
            className="primary"
            onClick={onPost}
            disabled={postDisabled || postPending}
            title={postTitle}
          >
            провести
          </button>
        ) : null
      )}

      {/* видалити — лише для збереженого DRAFT (не new, не проведено). */}
      {deletable && !isNew && !isPosted && (
        <button
          className="danger"
          onClick={onDelete}
          disabled={deleteDisabled || deletePending}
          title={deleteTitle}
        >
          видалити
        </button>
      )}

      {/* друк — лише де є друкована форма і документ збережено. Назву форми НЕ пишемо. */}
      {onPrint && !isNew && (
        <button onClick={onPrint}>друк</button>
      )}
    </div>
  );
}
